import angular from 'angular';
import { NsjRoutingType } from '@core/nsj-core';
import { LoadParams } from '@shared/models/loadparams.model';
import {
    IMovimentacao,
    IMovimentacaoCreateResponse,
    IMovimentacaoHistoricoDetalhe,
    IMovimentacaoHistoricoListQuery,
    IMovimentacaoHistoricoResumo,
    IMovimentacaoListFilters,
    IMovimentacaoListItem,
    IMovimentacaoListQuery,
    IMovimentacaoOffset,
    IMovimentacaoOrder,
    IMovimentacaoPayload,
    IMovimentacaoResumoItem,
    IMovimentacaoSituacaoFilterExpression,
    IMovimentacaoWorkflowEstadoFilterExpression,
    IMovimentacaoUpdatePayload,
    IMovimentacoesConstructor,
    IMovimentacoesMeta,
    IMovimentacoesPagination,
    MOVIMENTACOES_LOTE_LIMIT,
    MOVIMENTACOES_PAGE_LIMIT,
    TMovimentacaoOrderField
} from './models/movimentacoes.service.model';

type LoadOptions = {
    append?: boolean;
    silent?: boolean;
};

type SearchPayload = {
    search?: string;
    filters?: Record<string, any>;
} | null;

export class MovimentacoesService {
    static $inject = ['$rootScope', '$http', '$q', 'nsjRouting'];

    public entities: IMovimentacaoListItem[] = [];
    public loaded = false;
    public meta: IMovimentacoesMeta | null = null;
    public pagination: IMovimentacoesPagination = this.createPagination();
    public filters: IMovimentacaoListFilters = {};
    public order: IMovimentacaoOrder | null = null;
    public filter = '';
    public loadParams = new LoadParams();
    public loading_deferred: angular.IDeferred<unknown> | null = null;
    public constructors: IMovimentacoesConstructor = {};
    public after: IMovimentacaoOffset = {};

    constructor(
        public $rootScope: angular.IRootScopeService,
        public $http: angular.IHttpService,
        public $q: angular.IQService,
        public nsjRouting: NsjRoutingType,
    ) {
        this.loadParams.to_load = 1;
    }

    public setFilters(filters: IMovimentacaoListFilters & { filter?: string } = {}): void {
        const nextFilters = angular.copy(filters || {});

        if (Object.prototype.hasOwnProperty.call(nextFilters, 'filter')) {
            this.filter = typeof nextFilters.filter === 'string' ? nextFilters.filter : '';
            delete (nextFilters as { filter?: string }).filter;
        }

        this.filters = nextFilters;
    }

    public setFilterTerm(filter: string = ''): void {
        this.filter = typeof filter === 'string' ? filter : '';
    }

    public setOrder(order: IMovimentacaoOrder | null = null): void {
        this.order = order ? angular.copy(order) : null;
    }

    public getTrabalhadorFoto(identifier: string): angular.IPromise<string> {
        if (!identifier) {
            return this.$q.reject('ID do trabalhador é obrigatório');
        }

        return this.$q((resolve, reject) => {
            this.$http
                .get<ArrayBuffer>(
                    this.nsjRouting.generate('persona_trabalhadores_foto', { id: identifier }, true),
                    { responseType: 'arraybuffer' }
                )
                .then((response) => {
                    const mimeType = response.headers('Content-Type') || response.headers('content-type');
                    const file = new Blob([response.data], { type: mimeType });
                    const fileUrl = URL.createObjectURL(file);
                    resolve(fileUrl);
                })
                .catch((error) => reject(error));
        });
    }

    public resetState(): void {
        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        this.loading_deferred = null;
        this.entities = [];
        this.loaded = false;
        this.meta = null;
        this.pagination = this.createPagination();
        this.after = {};
        this.constructors = {};
        this.filters = {};
        this.order = null;
        this.filter = '';
        this.loadParams = new LoadParams();
        this.loadParams.to_load = 1;
    }

    public reload(): angular.IPromise<IMovimentacaoListItem[]> {
        this.pagination.currentPage = 1;
        this.after = {};
        this.loadParams.finished = false;

        return this.load(1);
    }

    public load(page: number = this.pagination.currentPage, options: LoadOptions = {}): angular.IPromise<IMovimentacaoListItem[]> {
        const targetPage = page > 0 ? Math.floor(page) : 1;
        const { append = false, silent = false } = options;

        if (append) {
            if (!this.loaded) {
                return this.load(1, { silent });
            }

            if (this.loadParams.finished) {
                return this.$q.resolve(this.entities);
            }

            if (!this.after || !Object.keys(this.after).length) {
                this.loadParams.finished = true;
                return this.$q.resolve(this.entities);
            }
        }

        if (!this.constructors.movimentacaoperiodo) {
            return this.rejectValidation('O parâmetro movimentacaoperiodo deve ser informado.');
        }

        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        this.loading_deferred = this.$q.defer();
        this.loadParams.busy = true;
        this.loadParams.finished = false;
        this.loadParams.to_load = 1;

        const queryParams = this.buildListQueryParams(this.constructors, append ? this.after : undefined);

        return this.$http
            .get<IMovimentacaoListItem[]>(
                this.nsjRouting.generate('movimentacoes_index', queryParams, true),
                { timeout: this.loading_deferred.promise }
            )
            .then((response) => this.handleLoadSuccess(response, targetPage, { append, silent }))
            .catch((error) => this.handleLoadError(error))
            .finally(() => {
                this.loadParams.busy = false;
                this.loadParams.to_load = 0;
                this.loading_deferred = null;
            });
    }

    public loadNextPage() {
        return this.load(this.pagination.currentPage + 1, { append: true });
    }

    public loadMore() {
        return this.loadNextPage();
    }

    public search(filter?: SearchPayload) {
        if (filter === null) {
            this.setFilterTerm('');
            this.setFilters({});
            return this.reload();
        }

        if (typeof filter !== 'undefined') {
            this.setFilterTerm(filter && typeof filter.search === 'string' ? filter.search : '');
            this.setFilters(this.normalizeLegacyFilters(filter && filter.filters ? filter.filters : {}));
        }

        return this.reload();
    }

    public get(identifier: string): angular.IPromise<IMovimentacao> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        return this.$q<IMovimentacao>((resolve, reject) => {
            this.$http
                .get<IMovimentacao>(this.nsjRouting.generate('movimentacoes_get', { id: identifier }, true))
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_get_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_get_error', error);
                    reject(error);
                });
        });
    }

    public create(payload: IMovimentacaoPayload): angular.IPromise<IMovimentacaoCreateResponse> {
        if (!payload || !this.isNonEmptyString(payload.movimentacaoperiodo)) {
            return this.rejectValidation('O campo movimentacaoperiodo deve ser informado.');
        }

        if (!this.isNonEmptyString(payload.trabalhador)) {
            return this.rejectValidation('O campo trabalhador deve ser informado.');
        }

        if (!this.isNonEmptyString(payload.evento)) {
            return this.rejectValidation('O campo evento deve ser informado.');
        }

        if (!this.isNonEmptyString(payload.lotacao)) {
            return this.rejectValidation('O campo lotacao deve ser informado.');
        }

        return this.$q<IMovimentacaoCreateResponse>((resolve, reject) => {
            this.$http
                .post<IMovimentacaoCreateResponse>(this.nsjRouting.generate('movimentacoes_create', {}, true), payload)
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_create_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_create_error', error);
                    reject(error);
                });
        });
    }

    public update(identifier: string, payload: IMovimentacaoUpdatePayload): angular.IPromise<void> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        return this.$q<void>((resolve, reject) => {
            this.$http
                .put<void>(this.nsjRouting.generate('movimentacoes_put', { id: identifier }, true), payload)
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_put_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_put_error', error);
                    reject(error);
                });
        });
    }

    public remove(identifier: string): angular.IPromise<void> {
        return this.performStateTransition('movimentacoes_delete', 'movimentacoes_delete_loaded', 'movimentacoes_delete_error', identifier, 'delete');
    }

    public enviar(identifier: string): angular.IPromise<void> {
        return this.performStateTransition('movimentacoes_enviar', 'movimentacoes_enviar_loaded', 'movimentacoes_enviar_error', identifier);
    }

    public efetuar(identifier: string): angular.IPromise<void> {
        return this.performStateTransition('movimentacoes_efetuar', 'movimentacoes_efetuar_loaded', 'movimentacoes_efetuar_error', identifier);
    }

    public cancelar(identifier: string): angular.IPromise<void> {
        return this.performStateTransition('movimentacoes_cancelar', 'movimentacoes_cancelar_loaded', 'movimentacoes_cancelar_error', identifier);
    }

    public recusar(identifier: string, motivo: string): angular.IPromise<void> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        const trimmedMotivo = typeof motivo === 'string' ? motivo.trim() : '';
        if (!trimmedMotivo) {
            return this.rejectValidation('O motivo da recusa deve ser informado.');
        }

        if (trimmedMotivo.length > 500) {
            return this.rejectValidation('O motivo da recusa deve ter no máximo 500 caracteres.');
        }

        return this.$q<void>((resolve, reject) => {
            this.$http
                .post<void>(
                    this.nsjRouting.generate('movimentacoes_recusar', { id: identifier }, true),
                    { motivo: trimmedMotivo }
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_recusar_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_recusar_error', error);
                    reject(error);
                });
        });
    }

    public excluirLote(movimentacaoperiodo: string, movimentacoes: string[]): angular.IPromise<void> {
        return this.performBatchAction(
            'movimentacoes_excluir_lote',
            'movimentacoes_excluir_lote_loaded',
            'movimentacoes_excluir_lote_error',
            movimentacaoperiodo,
            movimentacoes
        );
    }

    public enviarLote(movimentacaoperiodo: string, movimentacoes: string[]): angular.IPromise<void> {
        return this.performBatchAction(
            'movimentacoes_enviar_lote',
            'movimentacoes_enviar_lote_loaded',
            'movimentacoes_enviar_lote_error',
            movimentacaoperiodo,
            movimentacoes
        );
    }

    public cancelarLote(movimentacaoperiodo: string, movimentacoes: string[]): angular.IPromise<void> {
        return this.performBatchAction(
            'movimentacoes_cancelar_lote',
            'movimentacoes_cancelar_lote_loaded',
            'movimentacoes_cancelar_lote_error',
            movimentacaoperiodo,
            movimentacoes
        );
    }

    public getResumo(movimentacaoperiodo: string): angular.IPromise<IMovimentacaoResumoItem[]> {
        if (!this.isNonEmptyString(movimentacaoperiodo)) {
            return this.rejectValidation('O parâmetro movimentacaoperiodo deve ser informado.');
        }

        return this.$q<IMovimentacaoResumoItem[]>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoResumoItem[]>(
                    this.nsjRouting.generate('movimentacoes_resumo', { movimentacaoperiodo }, true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_resumo_loaded', response.data);
                    resolve(Array.isArray(response.data) ? response.data : []);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_resumo_error', error);
                    reject(error);
                });
        });
    }

    public listHistoricos(
        movimentacao: string,
        query: IMovimentacaoHistoricoListQuery = {}
    ): angular.IPromise<IMovimentacaoHistoricoResumo[]> {
        if (!this.isNonEmptyString(movimentacao)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        return this.$q<IMovimentacaoHistoricoResumo[]>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoHistoricoResumo[]>(
                    this.nsjRouting.generate('movimentacoes_index_historicos', {
                        id: movimentacao,
                        ...this.buildHistoricoQueryParams(query)
                    }, true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_index_historicos_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_index_historicos_error', error);
                    reject(error);
                });
        });
    }

    public getHistorico(movimentacao: string, historico: string): angular.IPromise<IMovimentacaoHistoricoDetalhe> {
        if (!this.isNonEmptyString(movimentacao)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        if (!this.isNonEmptyString(historico)) {
            return this.rejectValidation('O identificador do histórico deve ser informado.');
        }

        return this.$q<IMovimentacaoHistoricoDetalhe>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoHistoricoDetalhe>(
                    this.nsjRouting.generate('movimentacoes_get_historico', { id: movimentacao, historico }, true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_get_historico_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_get_historico_error', error);
                    reject(error);
                });
        });
    }

    private handleLoadSuccess(
        response: angular.IHttpResponse<IMovimentacaoListItem[]>,
        targetPage: number,
        options: LoadOptions
    ): IMovimentacaoListItem[] {
        const { append = false, silent = false } = options;
        const pageData = Array.isArray(response.data) ? response.data : [];
        const fullCount = this.extractFullCount(pageData);
        const hasNext = pageData.length === MOVIMENTACOES_PAGE_LIMIT;

        if (!append) {
            this.entities = [];
        }

        pageData.forEach((item) => this.entities.push(item));

        this.pagination.currentPage = targetPage;
        this.pagination.pageSize = MOVIMENTACOES_PAGE_LIMIT;
        if (fullCount !== null) {
            this.pagination.totalItems = fullCount;
        } else if (!append) {
            this.pagination.totalItems = this.entities.length;
        }

        this.meta = {
            fullCount,
            hasNext
        };
        this.loaded = true;
        this.loadParams.finished = !hasNext;
        this.after = angular.copy(this.resolveOffset(pageData) || {});

        const payload = {
            entities: this.entities,
            meta: this.meta,
            pagination: angular.copy(this.pagination),
            hasNext
        };

        if (!silent) {
            this.$rootScope.$broadcast('movimentacoes_index_loaded', payload);
            if (!hasNext) {
                this.$rootScope.$broadcast('movimentacoes_index_finished', payload);
            }
        }

        return this.entities;
    }

    private handleLoadError(error: any): IMovimentacaoListItem[] {
        if (error && error.xhrStatus === 'abort') {
            return this.entities;
        }

        this.loadParams.finished = true;
        if (!this.entities.length) {
            this.loaded = false;
            this.meta = null;
            this.pagination.totalItems = 0;
            this.after = {};
        }

        this.$rootScope.$broadcast('movimentacoes_index_error', error);
        throw error;
    }

    private performStateTransition(
        routeName: string,
        successEvent: string,
        errorEvent: string,
        identifier: string,
        method: 'post' | 'delete' = 'post'
    ): angular.IPromise<void> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        const request = method === 'delete'
            ? this.$http.delete<void>(this.nsjRouting.generate(routeName, { id: identifier }, true))
            : this.$http.post<void>(this.nsjRouting.generate(routeName, { id: identifier }, true), {});

        return this.$q<void>((resolve, reject) => {
            request
                .then((response) => {
                    this.$rootScope.$broadcast(successEvent, response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast(errorEvent, error);
                    reject(error);
                });
        });
    }

    private performBatchAction(
        routeName: string,
        successEvent: string,
        errorEvent: string,
        movimentacaoperiodo: string,
        movimentacoes: string[]
    ): angular.IPromise<void> {
        if (!this.isNonEmptyString(movimentacaoperiodo)) {
            return this.rejectValidation('O parâmetro movimentacaoperiodo deve ser informado.');
        }

        if (!Array.isArray(movimentacoes)) {
            return this.rejectValidation('O lote de movimentações deve ser informado.');
        }

        if (movimentacoes.length > MOVIMENTACOES_LOTE_LIMIT) {
            return this.rejectValidation(`Informe no máximo ${MOVIMENTACOES_LOTE_LIMIT} movimentações.`);
        }

        if (movimentacoes.some((movimentacao) => !this.isNonEmptyString(movimentacao))) {
            return this.rejectValidation('Todas as movimentações devem ser informadas.');
        }

        return this.$q<void>((resolve, reject) => {
            this.$http
                .post<void>(
                    this.nsjRouting.generate(routeName, { movimentacaoperiodo }, true),
                    { movimentacoes }
                )
                .then((response) => {
                    this.$rootScope.$broadcast(successEvent, response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast(errorEvent, error);
                    reject(error);
                });
        });
    }

    private buildListQueryParams(
        constructors: IMovimentacoesConstructor,
        offset?: IMovimentacaoOffset
    ): IMovimentacaoListQuery {
        const queryParams: IMovimentacaoListQuery = {
            ...constructors
        };
        const trimmedFilter = this.filter.trim();
        const order = this.getEffectiveOrder();
        const filters = this.filters || {};

        if (trimmedFilter) {
            queryParams.filter = trimmedFilter;
        }

        Object.keys(filters).forEach((key) => {
            const typedKey = key as keyof IMovimentacaoListFilters;
            const value = filters[typedKey];

            if (typedKey === 'situacao') {
                if (Array.isArray(value) && value.length) {
                    const situacaoFilters = value as IMovimentacaoSituacaoFilterExpression[];
                    queryParams.situacao = situacaoFilters.map((item) => ({
                        condition: item.condition,
                        value: item.value
                    }));
                }
                return;
            }

            if (typedKey === 'fase' && this.isMeaningfulValue(value)) {
                queryParams.fase = value as number | string;
                return;
            }

            if (typedKey === 'wkf_estado') {
                if (Array.isArray(value) && value.length) {
                    const workflowEstadoFilters = value as IMovimentacaoWorkflowEstadoFilterExpression[];
                    queryParams.wkf_estado = workflowEstadoFilters.map((item) => ({
                        condition: item.condition,
                        value: item.value
                    }));
                }
                return;
            }

            if (this.isMeaningfulValue(value)) {
                (queryParams as any)[typedKey] = value;
            }
        });

        if (Object.keys(order).length) {
            queryParams.order = order;
        }

        if (offset && Object.keys(offset).length) {
            queryParams.offset = offset;
        }

        return queryParams;
    }

    private buildHistoricoQueryParams(query: IMovimentacaoHistoricoListQuery): IMovimentacaoHistoricoListQuery {
        const params: IMovimentacaoHistoricoListQuery = {};

        if (this.isMeaningfulValue(query.tipo)) {
            params.tipo = query.tipo;
        }

        if (query.order && Object.keys(query.order).length) {
            params.order = query.order;
        } else {
            params.order = { ordem: 'ASC' };
        }

        if (query.offset && Object.keys(query.offset).length) {
            params.offset = query.offset;
        }

        return params;
    }

    private getEffectiveOrder(): IMovimentacaoOrder {
        const order = angular.copy(this.order || {});
        const functionalFields: TMovimentacaoOrderField[] = ['trabalhador.nome', 'evento.nome', 'lotacao.nome'];
        const hasFunctionalOrder = functionalFields.some((field) => Boolean(order[field]));

        if (hasFunctionalOrder && !order.ordem) {
            order.ordem = 'DESC';
        }

        if (!Object.keys(order).length) {
            order.ordem = 'DESC';
        }

        return order;
    }

    private resolveOffset(entities: IMovimentacaoListItem[]): IMovimentacaoOffset | null {
        if (!entities.length) {
            return null;
        }

        const order = this.getEffectiveOrder();
        const lastEntity = entities[entities.length - 1];
        const offset: IMovimentacaoOffset = {};

        (Object.keys(order) as TMovimentacaoOrderField[]).forEach((field) => {
            switch (field) {
                case 'trabalhador.nome':
                    offset[field] = lastEntity.trabalhador && lastEntity.trabalhador.nome
                        ? lastEntity.trabalhador.nome
                        : '';
                    break;
                case 'evento.nome':
                    offset[field] = lastEntity.evento && lastEntity.evento.nome
                        ? lastEntity.evento.nome
                        : '';
                    break;
                case 'lotacao.nome':
                    offset[field] = lastEntity.lotacao && lastEntity.lotacao.nome
                        ? lastEntity.lotacao.nome
                        : '';
                    break;
                default:
                    offset[field] = lastEntity.ordem;
                    break;
            }
        });

        return offset;
    }

    private extractFullCount(entities: IMovimentacaoListItem[]): number | null {
        if (!entities.length) {
            return this.meta ? this.meta.fullCount : 0;
        }

        const fullCount = entities[0].full_count;
        if (fullCount === null || typeof fullCount === 'undefined' || fullCount === '') {
            return this.meta ? this.meta.fullCount : null;
        }

        const numericValue = Number(fullCount);
        return Number.isFinite(numericValue) ? numericValue : null;
    }

    private normalizeLegacyFilters(filters: Record<string, any>): IMovimentacaoListFilters {
        const normalizedFilters: IMovimentacaoListFilters = {};

        Object.keys(filters || {}).forEach((key) => {
            const value = filters[key];
            if (!this.isMeaningfulValue(value)) {
                return;
            }

            if (key === 'situacao' && Array.isArray(value)) {
                normalizedFilters.situacao = value as IMovimentacaoSituacaoFilterExpression[];
                return;
            }

            if (key === 'wkf_estado' && Array.isArray(value)) {
                normalizedFilters.wkf_estado = value as IMovimentacaoWorkflowEstadoFilterExpression[];
                return;
            }

            if (key === 'fase') {
                (normalizedFilters as any)[key] = value;
                return;
            }

            (normalizedFilters as any)[key] = value;
        });

        return normalizedFilters;
    }

    private createPagination(): IMovimentacoesPagination {
        return {
            currentPage: 1,
            pageSize: MOVIMENTACOES_PAGE_LIMIT,
            totalItems: 0
        };
    }

    private rejectValidation<T>(message: string): angular.IPromise<T> {
        return this.$q.reject({ message });
    }

    private isNonEmptyString(value: unknown): value is string {
        return typeof value === 'string' && value.trim().length > 0;
    }

    private isMeaningfulValue(value: unknown): boolean {
        if (value === null || typeof value === 'undefined') {
            return false;
        }

        if (typeof value === 'string') {
            return value.trim().length > 0;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (typeof value === 'object') {
            return Object.keys(value as object).length > 0;
        }

        return true;
    }
}
